import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import LoginForm from './components/LoginForm';
import SecurityBadge from './components/SecurityBadge';
import LicenseCredentials from './components/LicenseCredentials';
import SessionWarning from './components/SessionWarning';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const AdminLogin = () => {
  const { user, loading, signIn, authError, clearError } = useAuth();
  const [loginState, setLoginState] = useState({
    isLoading: false,
    error: '',
    attemptCount: 0,
    isLocked: false,
    lockoutTime: null
  });
  
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Demo admin credentials for development - these should be real users in production
  const demoCredentials = {
    admin: { email: 'admin@sailorsbailbonds.com', password: 'SailorsAdmin2024!' },
    agent: { email: 'agent@sailorsbailbonds.com', password: 'SailorsAgent2024!' },
    operator: { email: 'operator@sailorsbailbonds.com', password: 'SailorsOp2024!' }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !loading) {
      window.location.href = '/database-management';
    }
  }, [user, loading]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Handle account lockout
  useEffect(() => {
    if (loginState?.isLocked && loginState?.lockoutTime) {
      const timer = setTimeout(() => {
        setLoginState(prev => ({
          ...prev,
          isLocked: false,
          lockoutTime: null,
          attemptCount: 0,
          error: ''
        }));
      }, 300000); // 5 minutes lockout

      return () => clearTimeout(timer);
    }
  }, [loginState?.isLocked, loginState?.lockoutTime]);

  const handleLogin = async (formData) => {
    if (loginState?.isLocked || loading) {
      return;
    }

    setLoginState(prev => ({ ...prev, isLoading: true, error: '' }));
    clearError();

    try {
      // Attempt Supabase authentication
      const result = await signIn(formData?.email || formData?.username, formData?.password);

      if (result?.success) {
        // Successful login - redirect will happen via useEffect
        setLoginState(prev => ({ 
          ...prev, 
          isLoading: false,
          attemptCount: 0,
          error: ''
        }));
      } else {
        // Handle authentication failure
        const newAttemptCount = loginState?.attemptCount + 1;
        
        if (newAttemptCount >= 3) {
          // Lock account after 3 failed attempts
          setLoginState(prev => ({
            ...prev,
            isLoading: false,
            error: 'Account locked due to multiple failed attempts. Please try again in 5 minutes.',
            attemptCount: newAttemptCount,
            isLocked: true,
            lockoutTime: new Date()
          }));
        } else {
          setLoginState(prev => ({
            ...prev,
            isLoading: false,
            error: result?.error || `Authentication failed. ${3 - newAttemptCount} attempts remaining.`,
            attemptCount: newAttemptCount
          }));
        }
      }
    } catch (error) {
      setLoginState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Login failed. Please try again.'
      }));
    }
  };

  const handleExtendSession = () => {
    setShowSessionWarning(false);
    // Session extension is handled automatically by Supabase
  };

  const handleLogout = () => {
    setShowSessionWarning(false);
    window.location.href = '/admin-login';
  };

  const formatDateTime = (date) => {
    return date?.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="Shield" size={24} color="white" />
          </div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  Admin Portal
                </h1>
                <p className="text-muted-foreground">
                  Secure Access to Database Management
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Calendar" size={16} />
              <span>{formatDateTime(currentTime)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Login Form Section */}
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-lg p-8 shadow-soft">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    Administrator Sign In
                  </h2>
                  <p className="text-muted-foreground">
                    Access database management and system administration tools
                  </p>
                </div>

                {(loginState?.isLocked || authError || loginState?.error) && (
                  <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                      <span className="text-sm font-medium text-error">
                        {loginState?.isLocked ? 'Account Temporarily Locked' : 'Authentication Error'}
                      </span>
                    </div>
                    <p className="text-sm text-error mt-1">
                      {loginState?.error || authError || 'Please check your credentials and try again.'}
                    </p>
                  </div>
                )}

                <LoginForm
                  onSubmit={handleLogin}
                  isLoading={loginState?.isLoading}
                  error={loginState?.error || authError}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <SecurityBadge />
              <LicenseCredentials />
              
              {/* System Status */}
              <div className="bg-card border border-border rounded-lg p-4 shadow-soft">
                <h3 className="text-sm font-medium text-foreground mb-3">
                  System Status
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Database</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-accent rounded-full"></div>
                      <span className="text-xs text-accent">Online</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Authentication</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-accent rounded-full"></div>
                      <span className="text-xs text-accent">Active</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Last Update</span>
                    <span className="text-xs text-muted-foreground">2 hours ago</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Session Warning Modal */}
      <SessionWarning
        isVisible={showSessionWarning}
        onExtend={handleExtendSession}
        onLogout={handleLogout}
        timeRemaining={300}
      />
    </div>
  );
};

export default AdminLogin;